import React from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { formatCountNoun } from '../../../domain/copy/countNoun';
import {
  formatConfirmedSafetyEventLabel,
  formatFunnelCount,
  formatPendingVerificationLabel,
  formatReviewedObservationLabel,
  SIGN_SAFETY_SCAN_COPY,
  type SafetyScanProductPresentation,
} from '../../../domain/acceptance/safetyScanProductPresentation';
import { REVIEW_REQUIRED_EPISODES_LABEL } from '../../../domain/acceptance/safetyScanWorkflowViewModel';

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      data-testid={`safety-scan-section-${id}`}
      sx={{ p: { xs: 2, md: 2.25 }, borderRadius: 2.5 }}
    >
      <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 0.12 }}>
        {title}
      </Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Paper>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function SafetyScanProblemSummary({
  model,
}: {
  model: SafetyScanProductPresentation['problem'];
}) {
  return (
    <Section id="problem" title="Safety problem">
      <Typography variant="h5" sx={{ fontWeight: 800 }}>{model.conditionLabel}</Typography>
      <Typography variant="body1" sx={{ mt: 0.5 }}>{model.locationLine}</Typography>
      <Box sx={{ mt: 1.5, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        <Fact label="Scan window" value={model.windowLabel} />
        <Fact label="Safety Case" value={model.caseLabel} />
        <Fact label="Site" value={model.siteLabel} />
        <Fact label="Camera" value={model.cameraLabel} />
        <Fact label="State" value={model.stateLabel} />
      </Box>
      {model.technicalIds.length ? (
        <details>
          <summary>
            <Typography component="span" variant="caption" color="text.secondary">Technical identifiers</Typography>
          </summary>
          <Typography variant="caption" color="text.secondary">{model.technicalIds.join(' · ')}</Typography>
        </details>
      ) : null}
    </Section>
  );
}

export function SafetyScanEvidenceReview({
  model,
  onVerify,
}: {
  model: SafetyScanProductPresentation['evidence'];
  onVerify?: () => void;
}) {
  const funnel = model.funnel;
  const excludedParts = [
    funnel.falsePositive != null ? formatCountNoun(funnel.falsePositive, 'false positive') : null,
    funnel.duplicate != null ? formatCountNoun(funnel.duplicate, 'duplicate') : null,
    funnel.needsMoreReview != null ? `${formatFunnelCount(funnel.needsMoreReview)} needing further review` : null,
    funnel.pending != null && funnel.pending > 0 ? `${formatFunnelCount(funnel.pending)} pending` : null,
  ].filter(Boolean);
  return (
    <Section id="evidence" title="Evidence review">
      <Typography variant="h6" sx={{ fontWeight: 800 }}>
        {formatReviewedObservationLabel(funnel.reviewed)}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.75 }}>
        {formatConfirmedSafetyEventLabel(funnel.confirmed)}
      </Typography>
      {excludedParts.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {excludedParts.join(' · ')}
        </Typography>
      ) : null}
      {model.pendingVerifyCount != null && model.pendingVerifyCount > 0 ? (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <Typography variant="body2">
            {formatPendingVerificationLabel(model.pendingVerifyCount)}
          </Typography>
          <Button
            variant="contained"
            onClick={onVerify}
            data-testid="safety-scan-verify-observations"
          >
            {REVIEW_REQUIRED_EPISODES_LABEL}
          </Button>
        </Stack>
      ) : model.verifyRequired && onVerify ? (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            onClick={onVerify}
            data-testid="safety-scan-verify-observations"
          >
            {REVIEW_REQUIRED_EPISODES_LABEL}
          </Button>
        </Stack>
      ) : null}
    </Section>
  );
}

export function SafetyScanPatternSummary({
  model,
  onOpenPattern,
}: {
  model: SafetyScanProductPresentation['pattern'];
  onOpenPattern?: () => void;
}) {
  return (
    <Section id="pattern" title="Recurring pattern">
      <Typography variant="body1" sx={{ fontWeight: 700 }}>{model.summary}</Typography>
      <Box sx={{ mt: 1.25, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        <Fact label="Location" value={model.locationLabel} />
        <Fact label="Recurrence" value={model.recurrenceLabel} />
        <Fact label="Scan window" value={model.windowLabel} />
      </Box>
      {model.href || onOpenPattern ? (
        <Button variant="text" sx={{ mt: 1 }} onClick={onOpenPattern} data-testid="safety-scan-pattern-link">
          View Pattern
        </Button>
      ) : null}
    </Section>
  );
}

export function SafetyScanControlSummary({
  model,
  onOpenCase,
}: {
  model: SafetyScanProductPresentation['control'];
  onOpenCase?: () => void;
}) {
  return (
    <Section id="control" title="Control">
      <Typography variant="body1" sx={{ fontWeight: 700 }}>{model.description}</Typography>
      <Box sx={{ mt: 1.25, display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        <Fact label="Category" value={model.categoryLabel} />
        <Fact label="Owner" value={model.ownerLabel} />
        <Fact label="Status" value={model.statusLabel} />
        <Fact label="Implemented" value={model.implementedLabel} />
      </Box>
      {onOpenCase ? (
        <Button variant="outlined" sx={{ mt: 1.5 }} onClick={onOpenCase} data-testid="safety-scan-open-case">
          View / update Control
        </Button>
      ) : null}
    </Section>
  );
}

export function SafetyScanFollowUpSummary({
  model,
}: {
  model: SafetyScanProductPresentation['followUp'];
}) {
  return (
    <Section id="followUp" title="Follow-up">
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        <Fact label="Measurement window" value={model.windowLabel} />
        <Fact label="Candidate observations" value={model.candidateLabel} />
        <Fact label="Reviewed in this Scan" value={model.reviewedConfirmedLabel} />
        <Fact label="Confirmed in this Scan window" value={model.observedAfterLabel} />
        <Fact label="Pending verification" value={model.pendingReviewLabel} />
        <Fact label="Coverage" value={model.coverageLabel} />
        <Fact label="Measurement status" value={model.statusLabel} />
      </Box>
      {model.advancedRawSignals ? (
        <details data-testid="safety-scan-followup-advanced" style={{ marginTop: 12 }}>
          <summary>
            <Typography component="span" variant="caption" color="text.secondary">
              {model.advancedRawSignals.heading}
            </Typography>
          </summary>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {model.advancedRawSignals.detail}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`Before Control: ${model.advancedRawSignals.baselineCount == null ? 'Not available' : model.advancedRawSignals.baselineCount}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`After Control: ${model.advancedRawSignals.followupCount == null ? 'Not available' : model.advancedRawSignals.followupCount}`}
          </Typography>
        </details>
      ) : null}
    </Section>
  );
}

export function SafetyScanConclusion({
  model,
}: {
  model: SafetyScanProductPresentation['conclusion'];
}) {
  return (
    <Section id="conclusion" title="Conclusion">
      <Typography variant="h6" sx={{ fontWeight: 800 }} data-testid="safety-scan-conclusion-outcome">
        {model.headline}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75 }}>{model.explanation}</Typography>
      {model.showPercent && model.percentReduction != null ? (
        <Typography variant="body2" sx={{ mt: 0.75 }} data-testid="safety-scan-percent-claim">
          {`${Math.abs(model.percentReduction)}% ${model.outcome === 'INCREASED' ? 'increase' : 'reduction'}`}
        </Typography>
      ) : /does not make a before\/after effectiveness claim/i.test(model.explanation) ? (
        <span data-testid="safety-scan-no-percent-claim" hidden />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} data-testid="safety-scan-no-percent-claim">
          No before/after effectiveness claim is made.
        </Typography>
      )}
    </Section>
  );
}

export function SafetyScanReadiness({
  model,
  onSign,
  signDisabled,
  signing,
}: {
  model: SafetyScanProductPresentation['readiness'];
  onSign?: () => void;
  signDisabled?: boolean;
  signing?: boolean;
}) {
  return (
    <Section id="readiness" title="Readiness + sign">
      <Typography variant="h6" sx={{ fontWeight: 800 }} data-testid="safety-scan-readiness-headline">
        {model.headline}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75 }}>{model.reviewCompleteLabel}</Typography>
      <Typography variant="body2">{model.controlRecordedLabel}</Typography>
      {model.items.filter((item) => !item.complete).map((item) => (
        <Typography key={item.key} variant="body2" sx={{ mt: 0.5 }}>
          {item.label}
        </Typography>
      ))}
      <Alert severity="info" sx={{ mt: 1.5 }} data-testid="safety-scan-sign-copy">
        {SIGN_SAFETY_SCAN_COPY.helper}
        {' '}
        {SIGN_SAFETY_SCAN_COPY.doesNotCloseCase}
        {' '}
        {SIGN_SAFETY_SCAN_COPY.doesNotStopMonitoring}
        {' '}
        {SIGN_SAFETY_SCAN_COPY.doesNotProveControl}
        {' '}
        {SIGN_SAFETY_SCAN_COPY.doesNotMakeComparable}
      </Alert>
      {onSign ? (
        <Button
          variant="contained"
          sx={{ mt: 1.5 }}
          disabled={signDisabled || !model.ready}
          onClick={onSign}
          data-testid="safety-scan-sign-button"
        >
          {signing ? 'Signing Safety Scan...' : SIGN_SAFETY_SCAN_COPY.button}
        </Button>
      ) : null}
    </Section>
  );
}

export function SafetyScanHistory({
  model,
}: {
  model: SafetyScanProductPresentation['history'];
}) {
  if (model.status === 'AVAILABLE_BUT_NOT_WIRED') {
    return (
      <Section id="history" title="Safety Scan history">
        <Typography variant="body2" color="text.secondary" data-testid="safety-scan-history-unwired">
          Compact history is available from Safety Scans / Records and is not wired into this Scan page.
        </Typography>
      </Section>
    );
  }
  if (model.status !== 'WIRED' || model.items.length === 0) return null;
  return (
    <Section id="history" title="Safety Scan history">
      <Stack spacing={0.75}>
        {model.items.map((item) => (
          <Typography key={item.label} variant="body2">{item.label}</Typography>
        ))}
      </Stack>
    </Section>
  );
}

export default function SafetyScanProductStory({
  model,
  onVerify,
  onOpenPattern,
  onOpenCase,
  onSign,
  signDisabled,
  signing,
  hideActions = false,
}: {
  model: SafetyScanProductPresentation;
  onVerify?: () => void;
  onOpenPattern?: () => void;
  onOpenCase?: () => void;
  onSign?: () => void;
  signDisabled?: boolean;
  signing?: boolean;
  hideActions?: boolean;
}) {
  return (
    <Stack spacing={1.5} data-testid="safety-scan-product-story">
      <SafetyScanProblemSummary model={model.problem} />
      <SafetyScanEvidenceReview model={model.evidence} onVerify={hideActions ? undefined : onVerify} />
      <SafetyScanPatternSummary model={model.pattern} onOpenPattern={hideActions ? undefined : onOpenPattern} />
      <SafetyScanControlSummary model={model.control} onOpenCase={hideActions ? undefined : onOpenCase} />
      <SafetyScanFollowUpSummary model={model.followUp} />
      <SafetyScanConclusion model={model.conclusion} />
      <SafetyScanReadiness
        model={model.readiness}
        onSign={hideActions ? undefined : onSign}
        signDisabled={signDisabled}
        signing={signing}
      />
      <SafetyScanHistory model={model.history} />
    </Stack>
  );
}
